import React, { useEffect, useState, useCallback } from "react";
import {
  Card, CardBody, Col, Container, Row, Button, Spinner, Alert, Badge,
  Modal, ModalHeader, ModalBody, ModalFooter, Label, Input, Form,
} from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";
import { ODATA_BASE } from "helpers/api_config";

import SayfaRehberi from "../../../components/Common/SayfaRehberi";
/**
 * Aktüel Anketleri — `Pool_Aktuel` tablosu.
 *
 * Menüdeki "Anket" sayfası `Pool` tablosunu yönetiyor ve ANA SAYFA
 * kategorisine (Esnaf Aktüel, Berlin Esnaf Sitesi...) bağlı.
 * Aktüel bölümünde gösterilen anketler ise `Pool_Aktuel` tablosunda ve
 * HABER kategorisine (Medya Aktüel, Dünya Aktüel, Spor Aktüel) bağlı;
 * bu tablo panelde hiç yönetilmiyordu.
 *
 * Redux'a dokunmamak için veri doğrudan OData'dan okunup yazılıyor
 * (Moderation sayfasıyla aynı yaklaşım).
 */

const api = async (path, options = {}) => {
  const rsp = await fetch(`${ODATA_BASE}/${path}`, {
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    ...options,
  });
  if (!rsp.ok) throw new Error("HTTP " + rsp.status);
  // DELETE gövdesiz dönebilir
  const text = await rsp.text();
  return text ? JSON.parse(text) : {};
};

const emptyForm = { ID: null, question: "", categoryId: "", isActive: true, answers: ["", ""] };

const PoolAktuel = () => {
  document.title = "Aktüel Anketleri | BERLİN ESNAF ADMIN PANELI";

  const [polls, setPolls] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, c] = await Promise.all([
        api("Pool_Aktuel?$expand=Pool_Aktuel_Answer,Pool_Aktuel_UsersSelected_Answer&$orderby=ID desc"),
        api("News_Category"),
      ]);
      setPolls(p.value || []);
      setCategories(c.value || []);
    } catch (e) {
      setError("Anketler yüklenemedi: " + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const categoryName = (id) => {
    const c = categories.find((x) => String(x.ID) === String(id));
    return c ? c.News_Category_Title : "—";
  };

  const openNew = () => { setForm(emptyForm); setModal(true); };

  const openEdit = (poll) => {
    setForm({
      ID: poll.ID,
      question: poll.Pool_Aktuel_Question_Text || "",
      categoryId: poll.News_Category_ID != null ? String(poll.News_Category_ID) : "",
      isActive: poll.Pool_Aktuel_IsActive !== false,
      answers: (poll.Pool_Aktuel_Answer || []).map((a) => a.Pool_Aktuel_Answer_Text),
    });
    setModal(true);
  };

  const setAnswer = (i, value) => {
    setForm((f) => {
      const answers = [...f.answers];
      answers[i] = value;
      return { ...f, answers };
    });
  };

  const save = async () => {
    const question = form.question.trim();
    const answers = form.answers.map((a) => a.trim()).filter(Boolean);

    if (!question) { setError("Soru metni boş olamaz."); return; }
    if (!form.categoryId) { setError("Bölüm seçiniz."); return; }
    if (answers.length < 2) { setError("En az 2 cevap girmelisiniz."); return; }

    setSaving(true);
    setError(null);
    try {
      const body = {
        Pool_Aktuel_Question_Text: question,
        News_Category_ID: parseInt(form.categoryId, 10),
        Pool_Aktuel_IsActive: form.isActive,
      };

      let pollId = form.ID;

      if (pollId) {
        await api(`Pool_Aktuel(${pollId})`, { method: "PATCH", body: JSON.stringify(body) });
        // Cevaplar yeniden yazılıyor: eskiden verilen oylar cevaba bağlı
        // olduğu için mevcut cevap satırları korunur, sadece metin güncellenir.
        const current = polls.find((p) => p.ID === pollId);
        const existing = (current && current.Pool_Aktuel_Answer) || [];

        for (let i = 0; i < answers.length; i++) {
          if (existing[i]) {
            await api(`Pool_Aktuel_Answer(${existing[i].ID})`, {
              method: "PATCH",
              body: JSON.stringify({ Pool_Aktuel_Answer_Text: answers[i] }),
            });
          } else {
            await api("Pool_Aktuel_Answer", {
              method: "POST",
              body: JSON.stringify({ Pool_Aktuel_ID: pollId, Pool_Aktuel_Answer_Text: answers[i] }),
            });
          }
        }
        // Fazla kalan eski cevaplar silinir
        for (let i = answers.length; i < existing.length; i++) {
          await api(`Pool_Aktuel_Answer(${existing[i].ID})`, { method: "DELETE" });
        }
        setInfo("Anket güncellendi.");
      } else {
        const created = await api("Pool_Aktuel", { method: "POST", body: JSON.stringify(body) });
        pollId = created && created.value && created.value[0] ? created.value[0].ID : null;

        // Pool_Aktuel controller'ı POST'ta boş dizi dönüyor (kayıt eklenmeden
        // önce sorgulanıyor). Kayıt gerçekte oluşuyor; ID'yi geri okuyoruz.
        if (!pollId) {
          const check = await api(
            "Pool_Aktuel?$orderby=ID desc&$top=1&$filter=" +
            encodeURIComponent(`News_Category_ID eq ${body.News_Category_ID}`)
          );
          const row = check.value && check.value[0];
          if (row && row.Pool_Aktuel_Question_Text === question) pollId = row.ID;
        }
        if (!pollId) throw new Error("Anket oluşturuldu ama ID alınamadı");

        for (const text of answers) {
          await api("Pool_Aktuel_Answer", {
            method: "POST",
            body: JSON.stringify({ Pool_Aktuel_ID: pollId, Pool_Aktuel_Answer_Text: text }),
          });
        }
        setInfo("Anket eklendi.");
      }

      setModal(false);
      await load();
    } catch (e) {
      setError("Kaydedilemedi: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    const poll = deleteTarget;
    setDeleteTarget(null);
    if (!poll) return;
    setSaving(true);
    try {
      // Önce cevaplar, sonra anket (yabancı anahtar kısıtı)
      for (const a of poll.Pool_Aktuel_Answer || []) {
        await api(`Pool_Aktuel_Answer(${a.ID})`, { method: "DELETE" });
      }
      await api(`Pool_Aktuel(${poll.ID})`, { method: "DELETE" });
      setInfo("Anket silindi.");
      await load();
    } catch (e) {
      setError("Silinemedi: " + e.message + " (anketin oyları varsa önce onlar silinmeli)");
    } finally {
      setSaving(false);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Yönetim" breadcrumbItem="Aktüel Anketleri" />
          <SayfaRehberi sayfa="poolaktuel" />

          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}
          {info && <Alert color="success" toggle={() => setInfo(null)}>{info}</Alert>}

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <div className="d-flex align-items-center mb-3 flex-wrap gap-2">
                    <h5 className="card-title mb-0 flex-grow-1">
                      Aktüel Bölümü Anketleri{" "}
                      <Badge color="primary">{polls.length}</Badge>
                    </h5>
                    <Button color="light" onClick={load} disabled={loading}>
                      <i className="bx bx-refresh align-middle me-1" />Yenile
                    </Button>
                    <Button color="primary" onClick={openNew}>
                      <i className="bx bx-plus align-middle me-1" />Yeni Anket
                    </Button>
                  </div>

                  <p className="text-muted font-size-13">
                    Buradaki anketler Aktüel bölümünde, seçtiğiniz haber kategorisinin
                    listesinde ilk sırada görünür. Ana sayfa bölümlerinin anketleri
                    (Esnaf Aktüel vb.) menüdeki <strong>Anket</strong> sayfasından yönetilir.
                  </p>

                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                    </div>
                  ) : polls.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="bx bx-poll display-4 text-muted" />
                      <h5 className="mt-3">Henüz anket yok</h5>
                      <p className="text-muted mb-0">"Yeni Anket" ile ekleyebilirsiniz.</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: "32%" }}>Soru</th>
                            <th style={{ width: "18%" }}>Bölüm</th>
                            <th>Cevaplar</th>
                            <th style={{ width: "9%" }}>Oy</th>
                            <th style={{ width: "9%" }}>Durum</th>
                            <th style={{ width: "12%" }} className="text-end">İşlemler</th>
                          </tr>
                        </thead>
                        <tbody>
                          {polls.map((p) => (
                            <tr key={p.ID}>
                              <td><strong>{p.Pool_Aktuel_Question_Text}</strong></td>
                              <td>
                                <span className="badge badge-soft-info">
                                  {categoryName(p.News_Category_ID)}
                                </span>
                              </td>
                              <td className="text-muted font-size-13">
                                {(p.Pool_Aktuel_Answer || []).map((a) => a.Pool_Aktuel_Answer_Text).join(" · ") || "—"}
                              </td>
                              <td>{(p.Pool_Aktuel_UsersSelected_Answer || []).length}</td>
                              <td>
                                <span className={"badge badge-soft-" + (p.Pool_Aktuel_IsActive !== false ? "success" : "secondary")}>
                                  {p.Pool_Aktuel_IsActive !== false ? "Aktif" : "Pasif"}
                                </span>
                              </td>
                              <td className="text-end">
                                <Button color="light" size="sm" className="me-2" onClick={() => openEdit(p)}>
                                  <i className="mdi mdi-pencil" />
                                </Button>
                                <Button color="danger" size="sm" outline onClick={() => setDeleteTarget(p)}>
                                  <i className="mdi mdi-delete" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Ekle / Düzenle */}
      <Modal isOpen={modal} toggle={() => setModal(false)} centered>
        <ModalHeader toggle={() => setModal(false)}>
          {form.ID ? "Anketi Düzenle" : "Yeni Anket"}
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={(e) => { e.preventDefault(); save(); }}>
            <div className="mb-3">
              <Label className="form-label">Bölüm</Label>
              <Input
                type="select"
                value={form.categoryId}
                onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
              >
                <option value="">Lütfen seçim yapınız</option>
                {categories.map((c) => (
                  <option key={c.ID} value={c.ID}>{c.News_Category_Title}</option>
                ))}
              </Input>
            </div>

            <div className="mb-3">
              <Label className="form-label">Soru</Label>
              <Input
                type="textarea"
                rows={2}
                placeholder="Anket sorusunu yazınız"
                value={form.question}
                onChange={(e) => setForm({ ...form, question: e.target.value })}
              />
            </div>

            <div className="mb-3">
              <Label className="form-label">Cevaplar (en az 2)</Label>
              {form.answers.map((a, i) => (
                <div className="d-flex align-items-center mb-2" key={i}>
                  <Input
                    type="text"
                    placeholder={`${i + 1}. cevap`}
                    value={a}
                    onChange={(e) => setAnswer(i, e.target.value)}
                  />
                  {form.answers.length > 2 && (
                    <Button
                      color="light" className="ms-2"
                      onClick={() => setForm((f) => ({ ...f, answers: f.answers.filter((_, j) => j !== i) }))}
                    >
                      <i className="mdi mdi-close" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                color="light" size="sm"
                onClick={() => setForm((f) => ({ ...f, answers: [...f.answers, ""] }))}
              >
                <i className="bx bx-plus align-middle me-1" />Cevap Ekle
              </Button>
            </div>

            <div className="form-check form-switch">
              <input
                type="checkbox"
                className="form-check-input"
                id="pollActive"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              <Label className="form-check-label" htmlFor="pollActive">Aktif</Label>
            </div>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setModal(false)} disabled={saving}>Vazgeç</Button>
          <Button color="primary" onClick={save} disabled={saving}>
            {saving ? <Spinner size="sm" /> : "Kaydet"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Silme onayı */}
      <Modal isOpen={!!deleteTarget} toggle={() => setDeleteTarget(null)} centered>
        <ModalHeader toggle={() => setDeleteTarget(null)}>Anket silinsin mi?</ModalHeader>
        <ModalBody>
          <p className="mb-2"><strong>{deleteTarget && deleteTarget.Pool_Aktuel_Question_Text}</strong></p>
          <p className="text-muted mb-0">
            Anket ve cevapları kalıcı olarak silinecek. Bu işlem geri alınamaz.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setDeleteTarget(null)}>Vazgeç</Button>
          <Button color="danger" onClick={remove}>Sil</Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default PoolAktuel;
